/* L'aperçu du document tel que le destinataire le recevra : la signature
   Omnix en tête, les colonnes du modèle, et rien de ce qu'il n'a pas à voir. */

import { Capsule } from "./primitives";
import { dateFr, entier, euro } from "@/lib/format";
import { colonnesDe, langueDe, porteDesPrix } from "@/domain/documents";
import type { ColonneDoc } from "@/domain/documents";
import type { LigneVente, ModeleDocument, Usine, Vente } from "@/types/metier";

export function Document({
  vente, lignes, modele, usine, destinataire,
}: {
  vente: Pick<Vente, "reference" | "date_document">;
  lignes: LigneVente[];
  modele: ModeleDocument;
  usine?: Usine | null;
  destinataire: string;
}) {
  const L = langueDe(modele.langue);
  const cols = colonnesDe(modele).filter((c) => c.visible);
  const chiffre = porteDesPrix(cols);
  const total = lignes.reduce((s, l) => s + Number(l.total), 0);
  const colis = lignes.reduce((s, l) => s + Number(l.qte), 0);
  const couleur = usine?.couleur || "#15201A";

  const cellule = (c: ColonneDoc, l: LigneVente) => {
    if (c.cle === "ref") return <td className="r" key={c.cle}>{l.ref || "—"}</td>;
    if (c.cle === "nom") return <td key={c.cle}>{l.nom}</td>;
    if (c.cle === "qte") return <td className="n" key={c.cle}>{entier(Number(l.qte))}</td>;
    if (c.cle === "unite") return <td key={c.cle}>{modele.unite ?? "—"}</td>;
    if (c.cle === "prix") return <td className="n" key={c.cle}>{euro(Number(l.prix))}</td>;
    return <td className="n" key={c.cle}>{euro(Number(l.total))}</td>;
  };

  return (
    <div className="doc">
      <div className="doc-h" style={{ borderBottomColor: couleur }}>
        <Capsule suffixe={null} taille={13} />
        <div className="fiche-t">
          <div className="doc-t">{modele.titre}</div>
          <div className="doc-s">{destinataire}</div>
        </div>
        <div className="doc-meta">
          <b>{vente.reference}</b>
          {L.date} {dateFr(vente.date_document)}
          <br />
          {L.incoterm} {modele.incoterm ?? "—"}
        </div>
      </div>

      <div className="doc-grille">
        <table>
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.cle} className={c.cle === "qte" || c.cle === "prix" || c.cle === "total" ? "n" : ""}>
                {c.libelle}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {lignes.map((l) => (
            <tr key={l.id}>{cols.map((c) => cellule(c, l))}</tr>
          ))}
        </tbody>
      </table>
        </div>

      <div className="doc-tot" style={{ borderTopColor: couleur }}>
        <span>
          {chiffre ? `${L.total} · ${lignes.length} ${L.lignes}` : `${lignes.length} ${L.lignes}`}
        </span>
        <span>
          {chiffre ? euro(total) : `${entier(colis)} ${(modele.unite ?? "").toLowerCase()}`}
        </span>
      </div>

      <div className="doc-f">
        {modele.mentions}
        <br />
        {L.emis} Omnix Trading
      </div>
    </div>
  );
}
